import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Style from "./CookieConsentBanner.module.css";
import {useCookieConsent} from "../../hooks/useCookieConsent";

// Renders the first-visit banner plus the "Cookie settings" panel that can be
// reopened at any time (see CookieSettingsFloatingButton). Never blocks the
// page: it is an overlay fragment, not a route gate, and analytics stays off
// until the user explicitly opts in via acceptAll/saveConsent.
function CookieConsentBanner() {
    const {t} = useTranslation('translation', {keyPrefix: 'cookieConsent'});
    const {hasDecided, analyticsAllowed, acceptAll, rejectOptional, saveConsent} = useCookieConsent();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [draftAnalytics, setDraftAnalytics] = useState(analyticsAllowed);

    const openSettings = () => {
        setDraftAnalytics(analyticsAllowed);
        setSettingsOpen(true);
    };

    useEffect(() => {
        const onReopen = () => openSettings();
        window.addEventListener("leela-reopen-cookie-settings", onReopen);
        return () => window.removeEventListener("leela-reopen-cookie-settings", onReopen);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analyticsAllowed]);

    if (settingsOpen) {
        return (
            <div className={Style.settingsOverlay} role="dialog" aria-modal="true" aria-labelledby="cookie-settings-title">
                <div className={Style.settingsPanel}>
                    <h2 id="cookie-settings-title">{t('settingsTitle')}</h2>
                    <p>{t('settingsIntro')}</p>

                    <div className={Style.settingsRow}>
                        <div>
                            <strong>{t('necessaryTitle')}</strong>
                            <div>{t('necessaryDescription')}</div>
                        </div>
                        <span>{t('alwaysOn')}</span>
                    </div>

                    <div className={Style.settingsRow}>
                        <div>
                            <label htmlFor="cookie-consent-analytics">
                                <strong>{t('analyticsTitle')}</strong>
                            </label>
                            <div>{t('analyticsDescription')}</div>
                        </div>
                        <input
                            id="cookie-consent-analytics"
                            type="checkbox"
                            checked={draftAnalytics}
                            onChange={(e) => setDraftAnalytics(e.target.checked)}
                        />
                    </div>

                    <p><a href="/datenschutz">{t('learnMore')}</a></p>

                    <div className={Style.settingsActions}>
                        <button
                            type="button"
                            className={Style.button}
                            onClick={() => {
                                saveConsent(false);
                                setSettingsOpen(false);
                            }}
                        >
                            {t('rejectOptional')}
                        </button>
                        <button
                            type="button"
                            className={`${Style.button} ${Style.buttonPrimary}`}
                            onClick={() => {
                                saveConsent(draftAnalytics);
                                setSettingsOpen(false);
                            }}
                        >
                            {t('save')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (hasDecided) {
        return null;
    }

    return (
        <div className={Style.banner} role="region" aria-label={t('settingsTitle')}>
            <div className={Style.text}>{t('bannerText')}</div>
            <div className={Style.actions}>
                <button type="button" className={Style.link} onClick={openSettings}>
                    {t('settings')}
                </button>
                <button type="button" className={Style.button} onClick={rejectOptional}>
                    {t('rejectOptional')}
                </button>
                <button type="button" className={`${Style.button} ${Style.buttonPrimary}`} onClick={acceptAll}>
                    {t('acceptAll')}
                </button>
            </div>
        </div>
    );
}

export default CookieConsentBanner;
